// ═══════════════════════════════════════════════════════════════════════════════
// FILE: src/app/services/session-health.service.ts
// VERSION: v1.0.0 – Proactive Session Health Monitor
// DATE: 2026-05-03
//
// PURPOSE:
//   Periodischer Heartbeat der NUR die Session-Gültigkeit prüft.
//   Bei 401 → sammelt Kontext (letzte URL, pslot, Zeitpunkt, Ursache)
//   und leitet kontrolliert zum Login weiter.
//
//   Dies ist der SINGLE POINT OF TRUTH für Session-Verlust-Erkennung.
//   CalStatusService und AuthErrorInterceptor delegieren hierhin.
//
// ARCHITECTURE:
//   SessionHealthService   → Layer 1 (Session)
//   CalStatusService       → Layer 2 (License) – darf nur feuern wenn Layer 1 ok
//   Guards / Routes        → Layer 3 (Business Logic)
//
// FEATURES:
//   ✅ Heartbeat alle 5 Minuten via /api/session/ping
//   ✅ Sofortige Session-Prüfung on-demand (checkNow())
//   ✅ Kontext-Sammlung bei Session-Loss (URL, pslot, datetime)
//   ✅ sessionExpired$ Observable für andere Services
//   ✅ Doppel-Dialog-Schutz (isHandlingExpiry flag)
//   ✅ "Return to last position" Link nach Re-Login
// ═══════════════════════════════════════════════════════════════════════════════

import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { BehaviorSubject, Subject, Subscription, interval } from 'rxjs';

// ═══════════════════════════════════════════════════════════════════════════════
// INTERFACES
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Kontext-Info die beim Session-Verlust gesammelt wird.
 * Wird als Query-Param an /login übergeben, damit der User nach Re-Login
 * zurück zu seiner letzten Position navigieren kann.
 */
export interface SessionLossContext {
  /** Letzte URL (full path) vor dem Rauswurf */
  lastUrl: string;

  /** Extrahierter pslot aus der URL (falls vorhanden) */
  lastPslot: string | null;

  /** Menschenlesbarer Seitenname (z.B. "Sprint Editor", "Board") */
  lastPageName: string;

  /** ISO-Timestamp wann der Rauswurf passiert ist */
  occurredAt: string;

  /**
   * Ursache: was hat den Rauswurf ausgelöst?
   *
   * Die ersten drei Werte sind gedeutet — sie stammen aus dem Fehlertext des Servers.
   * Ist keine Deutung möglich, steht hier der rohe HTTP-Status als `http_<n>`
   * (z. B. `http_0`, `http_502`) statt eines nichtssagenden `unknown`.
   * Grund: `unknown` in der Login-URL sagt weder dem Nutzer noch dem Protokoll etwas,
   * die Zahl grenzt die Ursache dagegen sofort ein (Task #410a, 15.08.2026).
   *
   * ⚠️ `http_0` heißt: die Anfrage hat den Server nie erreicht (Netz, CORS, Abbruch).
   * Das ist KEINE abgelaufene Sitzung — dass wir dennoch zum Login umleiten, ist der
   * offene Teil von Task #410.
   */
  reason: 'session_expired' | 'cookie_missing' | 'fingerprint_mismatch' | 'unknown' | `http_${number}`;

  /** HTTP Status Code des auslösenden Fehlers */
  httpStatus: number;

  /**
   * Wer hat den Rauswurf ausgelöst? Kennung der Aufrufstelle.
   *
   * Vier Aufrufer sind im Kompilat aktiv (geprüft 16.08.2026):
   *   `heartbeat-ping`      — der eigene Ping oben in diesem Service bekam 401
   *   `cal-silent-check`    — CalStatusService, stille Prüfung
   *   `cal-periodic-check`  — CalStatusService, periodische Prüfung
   *   `cal-status-guard`    — der Guard, als Absicherung gegen ein Wettrennen
   *
   * Zwei weitere Aufrufer existieren im Quellcode, sind aber **nicht eingebunden** und
   * können niemanden abmelden: `services/auth-error.interceptor.ts` (registriert ist
   * `interceptors/auth-error.interceptor.ts`, die ruft hier nicht an) und der
   * `SessionExpiryToastComponent`. Beide in `docs/TECH-DEBT.md` vermerkt.
   *
   * Warum die Angabe nötig ist: Am 15.08.2026 um 00:53 wurde der PO mit
   * `reason=unknown` zum Login geworfen — und im Serverprotokoll stand für dieses
   * Zeitfenster kein passender 401. Welcher der Aufrufer gefeuert hat, war im
   * Nachhinein nicht mehr feststellbar. Genau das schließt dieses Feld. (Task #410)
   */
  herkunft: string;

  /** Optionale Server-Message */
  serverMessage: string | null;
}

// ═══════════════════════════════════════════════════════════════════════════════
// SERVICE
// ═══════════════════════════════════════════════════════════════════════════════

@Injectable({
  providedIn: 'root'
})
export class SessionHealthService implements OnDestroy {

  private apiBaseUrl = this.constructApiBaseUrl();

  // ── Heartbeat ──────────────────────────────────────────────────────────
  private heartbeatSub: Subscription | null = null;
  private readonly HEARTBEAT_INTERVAL_MS = 5 * 60 * 1000; // 5 Minuten

  // ── State ──────────────────────────────────────────────────────────────
  private _isHandlingExpiry = false;

  /** true sobald eine Session-Expiry erkannt wurde (andere Services hören zu) */
  private sessionExpiredSubject = new BehaviorSubject<boolean>(false);
  public sessionExpired$ = this.sessionExpiredSubject.asObservable();

  /** Fired once with context when session loss is detected */
  private sessionLossContextSubject = new Subject<SessionLossContext>();
  public sessionLossContext$ = this.sessionLossContextSubject.asObservable();

  // ── TTL Warning ────────────────────────────────────────────────────────
  /** Schwellwert: ab wann wird der Toast angezeigt (in Sekunden) */
  private readonly WARNING_THRESHOLD_SECONDS = 5 * 60; // 5 Minuten

  /**
   * Emittet die verbleibenden Sekunden wenn TTL < WARNING_THRESHOLD.
   * null = kein Warning aktiv.
   * SessionExpiryToastComponent subscribt hierauf.
   */
  private sessionWarningSubject = new BehaviorSubject<number | null>(null);
  public sessionWarningSeconds$ = this.sessionWarningSubject.asObservable();

  /** Letzte bekannte TTL vom Server */
  private _lastTtl: number | null = null;

  constructor(
    private http: HttpClient,
    private router: Router
  ) {
    console.log('[SessionHealth] Initialized');
  }

  ngOnDestroy(): void {
    this.stopHeartbeat();
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // PUBLIC API
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Startet den periodischen Heartbeat.
   * Wird vom CalStatusGuard aufgerufen nachdem CAL-Check erfolgreich war.
   */
  startHeartbeat(): void {
    if (this.heartbeatSub) return; // bereits aktiv

    console.log(`[SessionHealth] Starting heartbeat (every ${this.HEARTBEAT_INTERVAL_MS / 60000} min)`);

    // Sofort einmal pingen (interval() feuert erst NACH dem ersten Intervall)
    this.ping();

    this.heartbeatSub = interval(this.HEARTBEAT_INTERVAL_MS).subscribe(() => {
      this.ping();
    });
  }

  stopHeartbeat(): void {
    if (this.heartbeatSub) {
      this.heartbeatSub.unsubscribe();
      this.heartbeatSub = null;
      console.log('[SessionHealth] Heartbeat stopped');
    }
  }

  /** Sofortige Prüfung (z.B. wenn ein Tab wieder aktiv wird) */
  checkNow(): void {
    this.ping();
  }

  /**
   * Wird vom AuthErrorInterceptor oder CalStatusService aufgerufen
   * wenn ein 401 erkannt wird. Zentrale Handling-Stelle.
   *
   * @param error  Der HTTP-Fehler der den 401 ausgelöst hat
   */
  handleSessionExpired(error: HttpErrorResponse, herkunft: string = 'unbekannt'): void {
    if (this._isHandlingExpiry) {
      console.log('[SessionHealth] Already handling expiry → skip');
      return;
    }

    this._isHandlingExpiry = true;
    this.sessionExpiredSubject.next(true);
    this.stopHeartbeat();

    const context = this.buildSessionLossContext(error, herkunft);
    this.sessionLossContextSubject.next(context);

    // D3 — sprechendes Log mit Parametern: diese eine Zeile muss reichen, um den
    // Rauswurf im Nachhinein zu erklären, auch wenn nur ein Bildschirmfoto vorliegt.
    console.warn(
      `[SessionHealth] Rauswurf: herkunft=${context.herkunft} reason=${context.reason} ` +
      `status=${context.httpStatus} seite="${context.lastPageName}" pslot=${context.lastPslot ?? '—'} ` +
      `serverMessage="${context.serverMessage ?? '—'}" at=${context.occurredAt}`,
      context
    );

    // ── Redirect zum Login mit Kontext ───────────────────────────────
    const returnParam = encodeURIComponent(context.lastUrl);
    const reasonParam = encodeURIComponent(context.reason);
    const pageParam = encodeURIComponent(context.lastPageName);
    const timeParam = encodeURIComponent(context.occurredAt);
    const fromParam = encodeURIComponent(context.herkunft);

    // Verwende window.location statt router.navigate für vollen State-Reset
    // `from=` ist neu (#410): ohne die Aufrufstelle war am 15.08. nicht feststellbar,
    // warum der Rauswurf geschah — im Serverprotokoll stand für den Zeitpunkt kein 401.
    const loginUrl = this.constructLoginUrl(
      `?returnTo=${returnParam}&reason=${reasonParam}&page=${pageParam}&at=${timeParam}&from=${fromParam}`
    );

    // Kurze Verzögerung damit der User den Dialog noch sieht
    // (der AuthErrorInterceptor zeigt ggf. noch einen Dialog)
    setTimeout(() => {
      window.location.href = loginUrl;
    }, 300);
  }

  /** Gibt true zurück wenn gerade ein Expiry-Handling läuft */
  get isHandlingExpiry(): boolean {
    return this._isHandlingExpiry;
  }

  /** Reset nach erfolgreichem Re-Login */
  resetState(): void {
    this._isHandlingExpiry = false;
    this.sessionExpiredSubject.next(false);
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // PRIVATE
  // ═══════════════════════════════════════════════════════════════════════════

  private ping(): void {
    this.http.get<{ status: string; ts: number; ttl?: number | null }>(
      `${this.apiBaseUrl}/api/session/ping`,
      { withCredentials: true }
    ).subscribe({
      next: (res) => {
        // Session lebt → TTL auswerten
        this._lastTtl = res.ttl ?? null;

        if (res.ttl !== null && res.ttl !== undefined && res.ttl > 0) {
          if (res.ttl <= this.WARNING_THRESHOLD_SECONDS) {
            // ── WARNING: Session läuft bald ab ──────────────────────
            console.warn(`[SessionHealth] ⚠️ TTL low: ${res.ttl}s remaining`);
            this.sessionWarningSubject.next(res.ttl);
          } else {
            // TTL ist OK → Warning aufheben (falls aktiv)
            if (this.sessionWarningSubject.value !== null) {
              this.sessionWarningSubject.next(null);
            }
          }
        }
      },
      error: (err: HttpErrorResponse) => {
        if (err.status === 401) {
          this.handleSessionExpired(err, 'heartbeat-ping');
        } else {
          // Netzwerk-Fehler, Server down etc. → kein Session-Problem
          console.warn('[SessionHealth] Ping failed (non-auth):', err.status);
        }
      }
    });
  }

  /**
   * Wird vom SessionExpiryToastComponent aufgerufen nachdem der User
   * "Stay Logged In" geklickt hat und der Extend-Ping erfolgreich war.
   */
  clearWarning(): void {
    this.sessionWarningSubject.next(null);
  }

  /** Gibt die letzte bekannte TTL zurück (für Debug/Logging) */
  get lastTtl(): number | null {
    return this._lastTtl;
  }

  /**
   * Baut den SessionLossContext aus dem aktuellen App-State
   */
  private buildSessionLossContext(error: HttpErrorResponse, herkunft: string): SessionLossContext {
    const currentUrl = this.router.url || window.location.pathname;

    // pslot aus URL extrahieren: /project/<pslot>/...
    const pslotMatch = currentUrl.match(/\/project\/([^/]+)/);
    const pslot = pslotMatch ? pslotMatch[1] : null;

    // Menschenlesbaren Seitennamen ableiten
    const pageName = this.derivePageName(currentUrl);

    // Ursache aus Server-Response ableiten
    const reason = this.deriveReason(error);

    return {
      lastUrl: currentUrl,
      lastPslot: pslot,
      lastPageName: pageName,
      occurredAt: new Date().toISOString(),
      reason,
      httpStatus: error.status,
      herkunft,
      serverMessage: error.error?.message || error.error?.error || null
    };
  }

  /**
   * Leitet aus der URL einen menschenlesbaren Seitennamen ab.
   */
  private derivePageName(url: string): string {
    // Bekannte Patterns → lesbare Namen
    const patterns: Array<[RegExp, string]> = [
      [/\/sprint\/\d+\/edit/,       'Sprint Editor'],
      [/\/sprint\/new\/edit/,       'Sprint Editor (New)'],
      [/\/item\/\d+\/edit/,        'Item Editor'],
      [/\/item\/new\/edit/,        'Item Editor (New)'],
      [/\/task\/\d+\/edit/,        'Task Editor'],
      [/\/board/,                  'Board'],
      [/\/backlog/,                'Backlog'],
      [/\/milestones/,             'Milestones'],
      [/\/target-canvas/,          'Target Canvas'],
      [/\/manage-access/,          'Access Management'],
      [/\/project-select/,         'Project Selection'],
      [/\/settings/,               'Settings'],
      [/\/persona/,                'Persona Maker'],
      [/\/dashboard/,              'Dashboard'],
      [/\/requirements/,           'Requirements'],
    ];

    for (const [pattern, name] of patterns) {
      if (pattern.test(url)) return name;
    }

    // Fallback: letztes URL-Segment bereinigen
    const segments = url.split('/').filter(Boolean);
    const last = segments[segments.length - 1] || 'Unknown Page';
    return last.replace(/-/g, ' ').replace(/^\w/, c => c.toUpperCase());
  }

  /**
   * Leitet die Ursache aus dem HTTP-Error-Body ab.
   * Backend schickt spezifische error codes/messages.
   *
   * Greift keine der drei Deutungen, wird NICHT `unknown` zurückgegeben, sondern der
   * rohe Status als `http_<n>`. Eine Begründung, die nichts begründet, ist schlechter
   * als eine rohe Zahl — sie landet über `handleSessionExpired()` in der Login-URL und
   * ist dort oft der einzige Hinweis, den wir im Nachhinein noch haben.
   * `unknown` bleibt nur für den Fall, dass gar kein Status vorliegt.
   */
  private deriveReason(error: HttpErrorResponse): SessionLossContext['reason'] {
    const body = error.error;

    if (typeof body === 'object' && body !== null) {
      const msg = (body.message || body.error || '').toLowerCase();
      const code = body.code || '';

      if (msg.includes('no ceeq_token') || msg.includes('cookie')) {
        return 'cookie_missing';
      }
      if (code === 'SESSION_INVALID' || msg.includes('session expired') || msg.includes('session invalid')) {
        return 'session_expired';
      }
      if (msg.includes('fingerprint')) {
        return 'fingerprint_mismatch';
      }
    }

    // Keine Deutung möglich → den rohen Status mitgeben statt ihn zu verschlucken.
    if (Number.isFinite(error.status)) {
      return `http_${error.status}` as SessionLossContext['reason'];
    }
    return 'unknown';
  }

  // ── URL Helpers ────────────────────────────────────────────────────────

  private constructApiBaseUrl(): string {
    const hostname = window.location.hostname;
    if (hostname.includes('localhost')) return 'http://localhost:3000';
    return `https://api.${hostname}`;
  }

  private constructLoginUrl(queryString: string): string {
    const hostname = window.location.hostname;
    if (hostname.includes('localhost')) return `http://localhost:4200/login${queryString}`;
    return `https://${hostname}/login${queryString}`;
  }
}
